import { z } from 'zod';
import { Sku } from './enums';

/**
 * Equipamento que o cliente compra por conta própria para usar o EtiquetaChef
 * (#1047). Nós não vendemos nem temos parceria com fornecedor: só recomendamos
 * modelos homologados, e o cliente pede a sugestão por e-mail.
 *
 * Origem: #1047 — 2026-10-27 18:06 +0100.
 *
 * Usado por: `equipmentForModules` (este arquivo); variante
 * `ONBOARDING_EQUIPMENT` de `EmailTemplateDataSchema` (`email.ts`); template
 * `apps/functions/src/email/templates/onboarding/equipment.tsx`.
 */
export const EquipmentKind = {
  PRINTER: 'PRINTER',
  BARCODE_READER: 'BARCODE_READER',
  NFC_TAGS: 'NFC_TAGS',
  SCALE: 'SCALE',
} as const;
export type EquipmentKind = (typeof EquipmentKind)[keyof typeof EquipmentKind];
export const ALL_EQUIPMENT_KINDS = Object.values(EquipmentKind) as [EquipmentKind, ...EquipmentKind[]];
export const EquipmentKindSchema = z.enum(ALL_EQUIPMENT_KINDS);

/**
 * Em que momento da vida do tenant o e-mail de equipamentos sai (#1047). Cada
 * etapa sai no máximo uma vez por tenant: é o id do doc do outbox
 * `tenants/{tid}/equipmentEmailRequests/{stage}` e parte da `idempotencyKey`.
 *
 * - `SELF_SERVICE_START` — tenant self-service nasceu (com ou sem trial: o
 *   adicional `ADDITIONAL` e a compra sem direito a trial não têm, e o texto
 *   segue o campo `trial` do dado, não o nome da etapa).
 * - `FIRST_PAID_CHARGE` — lembrete na 1ª cobrança paga de um self-service que
 *   teve trial (renovação paga na Stripe/Apple/Play).
 * - `ASSISTED_SALE` — pagamento da venda assistida do CRM, junto do
 *   `SALE_WELCOME`.
 *
 * Origem: #1047 — 2026-10-27 18:06 +0100.
 *
 * Usado por: `apps/functions/src/lib/equipmentEmail.ts` (pede),
 * `apps/functions/src/email/onEquipmentEmailRequested.ts` (envia), template
 * `onboarding/equipment.tsx` (texto por etapa).
 */
export const EquipmentEmailStage = {
  SELF_SERVICE_START: 'SELF_SERVICE_START',
  FIRST_PAID_CHARGE: 'FIRST_PAID_CHARGE',
  ASSISTED_SALE: 'ASSISTED_SALE',
} as const;
export type EquipmentEmailStage = (typeof EquipmentEmailStage)[keyof typeof EquipmentEmailStage];
export const ALL_EQUIPMENT_EMAIL_STAGES = Object.values(EquipmentEmailStage) as [
  EquipmentEmailStage,
  ...EquipmentEmailStage[],
];
export const EquipmentEmailStageSchema = z.enum(ALL_EQUIPMENT_EMAIL_STAGES);

/**
 * Módulo → equipamento que ele pede (#1047). A impressora não está aqui: ela é
 * obrigatória para todo tenant (`REQUIRED_EQUIPMENT`).
 *
 * Fora, de propósito:
 * - `MOD_TEMPERATURE` — o módulo (#368) ainda não existe; o sensor entra aqui
 *   quando ele for vendido.
 * - `MOD_SCALE` — balança Bluetooth aposentada (#1043). A Balança Inteligente
 *   (`MOD_SCALE_AI`) lê o visor pela câmera, mas pede uma balança com visor
 *   digital — por isso é ela que mapeia para `SCALE`.
 */
const MODULE_EQUIPMENT: readonly (readonly [string, EquipmentKind])[] = [
  [Sku.MOD_SCANNER, EquipmentKind.BARCODE_READER],
  [Sku.MOD_NFC_TAGS, EquipmentKind.NFC_TAGS],
  [Sku.MOD_SCALE_AI, EquipmentKind.SCALE],
];

/** Equipamento que todo estabelecimento precisa, com qualquer plano. */
export const REQUIRED_EQUIPMENT: readonly EquipmentKind[] = [EquipmentKind.PRINTER];

/**
 * Equipamento opcional que os módulos contratados pedem, na ordem fixa de
 * `MODULE_EQUIPMENT` e sem repetição (#1047).
 *
 * Origem: #1047 — 2026-10-27 18:06 +0100.
 *
 * Por quê: o self-service concede TODOS os módulos (`IAP_BUNDLE`), então o
 * e-mail não pode dizer "você precisa de tudo isto": a impressora é
 * obrigatória (`REQUIRED_EQUIPMENT`) e o resto sai como "se usar o módulo X".
 *
 * Contrato: recebe as capabilities efetivas do tenant
 * (`billing/subscription.capabilities`, lista de SKU); SKU desconhecido é
 * ignorado. Função pura.
 *
 * Usado por: `apps/functions/src/lib/equipmentEmail.ts` (`buildEquipmentEmail`).
 */
export function equipmentForModules(skus: readonly string[]): EquipmentKind[] {
  const owned = new Set(skus);
  return MODULE_EQUIPMENT.filter(([sku]) => owned.has(sku)).map(([, kind]) => kind);
}

/** Caixa do suporte do Brasil (#661). */
export const SUPPORT_EMAIL_BR = 'suporte@etiquetachef.com';
/** Caixa do suporte dos demais países (#1047: owner confirmou que existe). */
export const SUPPORT_EMAIL_INTL = 'support@etiquetachef.com';

/**
 * Para onde o cliente escreve pedindo sugestão de equipamento (#1047).
 *
 * Origem: #1047 — 2026-10-27 18:06 +0100 (decisão do owner na conversa do
 * planejamento).
 *
 * Por quê: regra única, decidida pelo owner — tenant de parceiro com suporte
 * cadastrado (#968) → o e-mail do parceiro; tenant BR → `suporte@`; qualquer
 * outro país → `support@`. Vale só para o e-mail de equipamentos e para o aviso
 * das telas de Impressoras e Leitores; os outros e-mails seguem com `suporte@`.
 *
 * Contrato: `countryCode` é `tenants/{tid}.countryCode` (ISO-3166 alfa-2,
 * maiúsculo); `null`/ausente cai em `support@`. `partnerEmail` vazio ou só
 * espaço é tratado como ausente. Função pura.
 *
 * Usado por: `apps/functions/src/lib/equipmentEmail.ts` (Reply-To e rodapé do
 * e-mail). **Espelhos à mão** (duplicação intencional, CLAUDE.md): Android
 * `domain/support/EquipmentContact.kt` e iOS
 * `Packages/Domain/Sources/Config/SupportContact.swift`
 * (`SupportContact.equipmentEmail`). Mudou a regra aqui → mude os dois.
 */
export function equipmentContactEmail(args: {
  countryCode: string | null | undefined;
  partnerEmail?: string | null;
}): string {
  const partner = args.partnerEmail?.trim() ?? '';
  if (partner !== '') return partner;
  return args.countryCode?.toUpperCase() === 'BR' ? SUPPORT_EMAIL_BR : SUPPORT_EMAIL_INTL;
}

/**
 * Pedido de envio do e-mail de equipamentos — o outbox (#1047).
 *
 * Origem: #1047 — 2026-10-27 18:06 +0100.
 *
 * Dados — `tenants/{tid}/equipmentEmailRequests/{stage}`, escrito só pelo
 * servidor (rules: nenhum `match`, então negado ao cliente):
 *   { "stage": "SELF_SERVICE_START", "created_at": <serverTimestamp>,
 *     "status": "SENT", "sent_at": <serverTimestamp> }
 * `status` ausente = pendente (o trigger ainda não terminou); `SKIPPED` traz
 * `skip_reason`. O doc é criado com `create()` — o segundo pedido da mesma
 * etapa dá `ALREADY_EXISTS` e é ignorado, o que faz de cada etapa um evento
 * único na vida do tenant.
 *
 * Usado por: `apps/functions/src/lib/equipmentEmail.ts` (cria),
 * `apps/functions/src/email/onEquipmentEmailRequested.ts` (lê e atualiza).
 */
export const EquipmentEmailRequestStatus = {
  SENT: 'SENT',
  SKIPPED: 'SKIPPED',
} as const;
export type EquipmentEmailRequestStatus =
  (typeof EquipmentEmailRequestStatus)[keyof typeof EquipmentEmailRequestStatus];

export const EquipmentEmailRequestSchema = z.object({
  stage: EquipmentEmailStageSchema,
  status: z.enum([EquipmentEmailRequestStatus.SENT, EquipmentEmailRequestStatus.SKIPPED]).optional(),
  skip_reason: z.string().optional(),
});
export type EquipmentEmailRequest = z.infer<typeof EquipmentEmailRequestSchema>;
